import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import EmailIcon from '@mui/icons-material/Email'
import EventIcon from '@mui/icons-material/Event'
import PhoneIcon from '@mui/icons-material/Phone'
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded'
import Search from '@mui/icons-material/Search'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import API from '../../services/API'
import { GlobalContext, UserContext } from '../../App'
import { currencySymbol } from '../../helpers/region'

// Rendered inside a `display: flex` Grid item so the tile stretches to the
// row height — don't give it a percentage height: a percent-height child
// directly under a spaced MuiGrid item resolves against the wrong box and
// overflows into the next row.
const StatTile = ({ label, value, caption }) => (
  <Card variant="outlined" sx={{ p: 1.5, width: '100%' }}>
    <Typography variant="body2" color="text.secondary" noWrap>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    )}
  </Card>
)

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')

const managerName = (org) =>
  `${org.accountManager?.firstName ?? ''} ${org.accountManager?.lastName ?? ''}`.trim()

const getEngagementStatus = (org) => {
  if (!org.engagementExpiry)
    return { label: 'No engagement letter', color: 'default' }
  const expiry = moment(org.engagementExpiry)
  if (expiry.isBefore(moment(), 'day'))
    return {
      label: `Letter expired ${expiry.format('DD/MM/YY')}`,
      color: 'error',
    }
  if (expiry.diff(moment(), 'days') <= 60)
    return {
      label: `Letter expires ${expiry.format('DD/MM/YY')}`,
      color: 'warning',
    }
  return {
    label: `Letter valid to ${expiry.format('DD/MM/YY')}`,
    color: 'success',
  }
}

const ClientCard = ({ org, currency, userID }) => {
  const navigate = useNavigate()
  const engagement = getEngagementStatus(org)
  const stats = org.stats || {}

  return (
    <Card
      variant="outlined"
      sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Stack spacing={1.25} flex={1}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              bgcolor: org.color || 'anBlue.main',
              fontWeight: 700,
              width: 44,
              height: 44,
            }}
          >
            {getInitials(org.orgName)}
          </Avatar>
          <Box minWidth={0} flex={1}>
            <Typography sx={{ fontWeight: 800 }} noWrap>
              {org.orgName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[
                org.orgType && `${org.orgType} client`,
                org.baseLocation?.name,
                org.orgAddressTown || org.orgAddressCity,
              ]
                .filter(Boolean)
                .join(' · ') || '—'}
            </Typography>
          </Box>
          {org.archived && (
            <Chip size="small" label="Archived" sx={{ borderRadius: 1 }} />
          )}
        </Stack>

        <Stack direction="row" spacing={2.5} alignItems="center">
          <Box>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {stats.upcomingEvents ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              upcoming
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {stats.totalEvents ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              total events
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {stats.contactCount ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              contacts
            </Typography>
          </Box>
        </Stack>

        <Typography variant="caption" color="text.secondary" noWrap>
          <EventIcon
            sx={{ fontSize: 14, mb: '-2px', mr: 0.5 }}
            color="inherit"
          />
          {stats.nextEvent ? (
            <RouterLink
              to={`/events/${stats.nextEvent._id}`}
              style={{ color: 'inherit' }}
            >
              {`Next: ${moment(stats.nextEvent.startDate).format(
                'Do MMM YYYY',
              )} — ${stats.nextEvent.title}`}
            </RouterLink>
          ) : (
            'No upcoming events'
          )}
        </Typography>

        <Typography variant="caption" color="text.secondary" noWrap>
          <SupportAgentRoundedIcon
            sx={{ fontSize: 14, mb: '-2px', mr: 0.5 }}
            color="inherit"
          />
          {org.accountManager?._id
            ? `Account manager: ${managerName(org)}${
                org.accountManager._id === userID ? ' (you)' : ''
              }`
            : 'No account manager'}
        </Typography>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {org.orgType === 'Events' && (
            <Chip
              size="small"
              label={engagement.label}
              color={engagement.color}
              variant={engagement.color === 'default' ? 'outlined' : 'filled'}
              sx={{ borderRadius: 1, fontWeight: 700 }}
            />
          )}
          {stats.unpaidCount > 0 && (
            <Chip
              size="small"
              label={`${currency}${Math.round(
                stats.unpaidTotal,
              ).toLocaleString()} outstanding · ${stats.unpaidCount} invoice${
                stats.unpaidCount === 1 ? '' : 's'
              }`}
              color="error"
              sx={{ borderRadius: 1, fontWeight: 700 }}
            />
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mt: 'auto' }}
        >
          <Button size="small" onClick={() => navigate(org._id)} variant='outlined' color='contrast'>
            Open
          </Button>
          <Box flex={1} />
          {org.orgEmail && (
            <Tooltip title={org.orgEmail}>
              <IconButton size="small" href={`mailto:${org.orgEmail}`}>
                <EmailIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {org.orgTel && (
            <Tooltip title={org.orgTel}>
              <IconButton size="small" href={`tel:${org.orgTel}`}>
                <PhoneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}

const Clients = () => {
  const navigate = useNavigate()
  const { tenantInfo } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const currency = currencySymbol(tenantInfo?.region)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [clients, setClients] = useState([])
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('name')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [managedBy, setManagedBy] = useState('all')

  const fetchClients = () => {
    setLoading(true)
    setFetchError(false)
    API.get('/event-planning/organisations')
      .then((res) => {
        if (res.status === 200) {
          setClients(res.data.clients)
        } else {
          setFetchError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchClients()
  }, [])

  const active = useMemo(
    () => clients.filter((org) => !org.archived),
    [clients],
  )

  const totals = useMemo(
    () => ({
      clients: active.length,
      upcomingEvents: active.reduce(
        (sum, org) => sum + (org.stats?.upcomingEvents || 0),
        0,
      ),
      outstanding: active.reduce(
        (sum, org) => sum + (org.stats?.unpaidTotal || 0),
        0,
      ),
      unpaidInvoices: active.reduce(
        (sum, org) => sum + (org.stats?.unpaidCount || 0),
        0,
      ),
      expiredLetters: active.filter(
        (org) =>
          org.engagementExpiry &&
          moment(org.engagementExpiry).isBefore(moment(), 'day'),
      ).length,
      expiringLetters: active.filter(
        (org) =>
          org.engagementExpiry &&
          !moment(org.engagementExpiry).isBefore(moment(), 'day') &&
          moment(org.engagementExpiry).diff(moment(), 'days') <= 60,
      ).length,
    }),
    [active],
  )

  const visibleClients = useMemo(() => {
    const query = search.trim().toLowerCase()
    const matches = (value) => value?.toLowerCase().includes(query)
    const filtered = clients.filter(
      (org) =>
        (includeArchived || !org.archived) &&
        (managedBy === 'all' ||
          (managedBy === 'me' && org.accountManager?._id === user?._id) ||
          (managedBy === 'unassigned' && !org.accountManager?._id)) &&
        (!query ||
          matches(managerName(org)) ||
          matches(org.orgName) ||
          matches(org.orgType) ||
          matches(org.baseLocation?.name) ||
          matches(org.orgAddressTown) ||
          matches(org.orgAddressCity) ||
          matches(org.orgEmail)),
    )
    const FAR_FUTURE = 8640000000000000
    const sorters = {
      name: (a, b) => (a.orgName || '').localeCompare(b.orgName || ''),
      nextEvent: (a, b) =>
        (a.stats?.nextEvent
          ? new Date(a.stats.nextEvent.startDate).getTime()
          : FAR_FUTURE) -
        (b.stats?.nextEvent
          ? new Date(b.stats.nextEvent.startDate).getTime()
          : FAR_FUTURE),
      outstanding: (a, b) =>
        (b.stats?.unpaidTotal || 0) - (a.stats?.unpaidTotal || 0),
      engagement: (a, b) =>
        (a.engagementExpiry
          ? new Date(a.engagementExpiry).getTime()
          : FAR_FUTURE) -
        (b.engagementExpiry
          ? new Date(b.engagementExpiry).getTime()
          : FAR_FUTURE),
    }
    return [...filtered].sort(sorters[sortBy] || sorters.name)
  }, [clients, search, sortBy, includeArchived, managedBy, user?._id])

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={9}>
          <Typography variant="title2">Clients</Typography>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Tooltip
              title={includeArchived ? 'Hide Archived' : 'Show Archived'}
            >
              <IconButton
                color={includeArchived ? 'anGreen' : 'inherit'}
                onClick={() => setIncludeArchived((prev) => !prev)}
              >
                {includeArchived ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('new')}
            >
              Add New
            </Button>
          </Box>
        </Grid>

        {loading && (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        )}
        {fetchError && (
          <Grid item xs={12}>
            <Alert severity="error" variant="filled">
              Unable to load Clients. Please try again or contact AmbuNet
              support.
            </Alert>
          </Grid>
        )}

        {!loading && !fetchError && (
          <>
            <Grid item xs={6} sm={3} display="flex">
              <StatTile label="Clients" value={totals.clients} />
            </Grid>
            <Grid item xs={6} sm={3} display="flex">
              <StatTile
                label="Upcoming events"
                value={totals.upcomingEvents}
                caption="Booked in from today"
              />
            </Grid>
            <Grid item xs={6} sm={3} display="flex">
              <StatTile
                label="Outstanding"
                value={`${currency}${Math.round(
                  totals.outstanding,
                ).toLocaleString()}`}
                caption={`${totals.unpaidInvoices} invoice${
                  totals.unpaidInvoices === 1 ? '' : 's'
                } awaiting payment`}
              />
            </Grid>
            <Grid item xs={6} sm={3} display="flex">
              <StatTile
                label="Expired engagement letters"
                value={totals.expiredLetters}
                caption={`${totals.expiringLetters} expiring within 60 days`}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Box height='100%'>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            component={Paper}
            padding={2}
          >
            <TextField
              size="small"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="none"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              size="small"
              label="Account manager"
              value={managedBy}
              onChange={(e) => setManagedBy(e.target.value)}
              sx={{ minWidth: 180, flex: 0.3 }}
            >
              <MenuItem value="all">Anyone</MenuItem>
              <MenuItem value="me">My clients</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Sort by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{ minWidth: 180, flex: 0.3 }}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="nextEvent">Next event</MenuItem>
              <MenuItem value="outstanding">Outstanding balance</MenuItem>
              <MenuItem value="engagement">Engagement letter expiry</MenuItem>
            </TextField>
          </Stack></Box>
        </Grid>

        {!loading && !fetchError && visibleClients.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">
              No clients found
              {search || managedBy !== 'all' ? ' for these filters' : ''}. Use
              Add New to create one.
            </Alert>
          </Grid>
        )}

        {visibleClients.map((org) => (
          <Grid item xs={12} md={6} xl={4} key={org._id} display="flex">
            <ClientCard org={org} currency={currency} userID={user?._id} />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default Clients
